// Photo upload helpers for Storage Inventory.
//
// Photos are uploaded via the inventory API and assets store only the returned
// storage key in their `images` array — never the image bytes. Locally the
// bytes are written to disk under .data/storage-inventory/photos/; the asset
// record just keeps the key, so the SQLite rows stay small.

import { storageInventoryApi } from './storageInventoryClient';
import type { AssetCategory } from '@/types/storageInventory';

// Accepted image MIME types for upload.
export const ACCEPTED_IMAGE_RE = /^image\/(png|jpeg|jpg)$/;

/** Read a File as a base64 string (without the data: URI prefix). */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/** Upload a single image file and return its stored key. */
export async function uploadPhotoFile(category: AssetCategory, file: File): Promise<string> {
  const data = await fileToBase64(file);
  const { key } = await storageInventoryApi.uploadPhoto(category, {
    data,
    contentType: file.type,
    filename: file.name,
  });
  return key;
}

/**
 * Upload several image files, returning their storage keys in order.
 * Non-image files are skipped. Uploads run sequentially to keep request
 * volume predictable for the dev proxy.
 */
export async function uploadPhotoFiles(
  category: AssetCategory,
  files: FileList | File[]
): Promise<string[]> {
  const list = Array.from(files).filter((f) => ACCEPTED_IMAGE_RE.test(f.type));
  const keys: string[] = [];
  for (const file of list) {
    keys.push(await uploadPhotoFile(category, file));
  }
  return keys;
}
